import { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart2, TrendingUp, TrendingDown, DollarSign, Users, Package, Sparkles, ChevronUp, ChevronDown, Target, Zap, AlertTriangle, ArrowRight } from 'lucide-react';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];
const revenue = [2.8, 3.1, 2.9, 3.6, 3.9, 3.8, 4.2];
const expenses = [1.4, 1.5, 1.4, 1.7, 1.8, 1.7, 1.9];
const profit = revenue.map((r, i) => parseFloat((r - expenses[i]).toFixed(2)));

const topProducts = [
  { name: 'Hikvision CCTV Systems', sales: 48, revenue: 1840000, trend: 18, color: 'bg-blue-500' },
  { name: 'Dahua IP Cameras', sales: 36, revenue: 980000, trend: 12, color: 'bg-violet-500' },
  { name: 'Paradox Alarm Systems', sales: 22, revenue: 650000, trend: -5, color: 'bg-amber-500' },
  { name: 'MikroTik Networking', sales: 19, revenue: 480000, trend: 34, color: 'bg-green-500' },
  { name: 'Starlink Solutions', sales: 8, revenue: 420000, trend: 340, color: 'bg-sky-500' },
  { name: 'TP-Link WiFi', sales: 31, revenue: 310000, trend: 8, color: 'bg-pink-500' },
];

const techPerformance = [
  { name: 'Kamal Perera', jobs: 24, revenue: 1240000, rating: 4.9, trend: 'up' },
  { name: 'Nimal Silva', jobs: 19, revenue: 980000, rating: 4.8, trend: 'up' },
  { name: 'Suresh Kumar', jobs: 21, revenue: 850000, rating: 4.7, trend: 'down' },
  { name: 'Dilshan Fernando', jobs: 16, revenue: 720000, rating: 4.9, trend: 'up' },
];

const aiInsights = [
  { type: 'opportunity', icon: TrendingUp, color: 'text-green-600 bg-green-50 border-green-200', title: 'Starlink Demand Surge', desc: '340% increase in inquiries. Recommend increasing stock by 5 units this month.' },
  { type: 'warning', icon: AlertTriangle, color: 'text-amber-600 bg-amber-50 border-amber-200', title: 'Average Deal Size Declining', desc: 'Down 3.6% — consider bundling CCTV + installation + maintenance contracts.' },
  { type: 'insight', icon: Sparkles, color: 'text-violet-600 bg-violet-50 border-violet-200', title: 'Q3 Seasonality Peak Ahead', desc: 'Historical data shows 15-20% revenue surge in Aug-Sep. Plan inventory accordingly.' },
  { type: 'action', icon: Target, color: 'text-blue-600 bg-blue-50 border-blue-200', title: 'Maintenance Contract Opportunity', desc: '67 customers with expired warranties. Launch renewal campaign = est. LKR 420K.' },
];

const forecast = [
  { month: 'Aug 2026', revenue: 4800000, confidence: 87 },
  { month: 'Sep 2026', revenue: 5200000, confidence: 74 },
  { month: 'Oct 2026', revenue: 4600000, confidence: 61 },
];

function MiniChart({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 200;
  const height = 50;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 10) - 5;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-12">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BarChartMini({ data, labels, color }: { data: number[]; labels: string[]; color: string }) {
  const max = Math.max(...data);
  return (
    <div className="flex items-end gap-1 h-20 mt-2">
      {data.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: `${(v / max) * 100}%` }}
            transition={{ duration: 0.6, delay: i * 0.05 }}
            className={`w-full rounded-t-sm ${color} opacity-80`}
          />
          <span className="text-[9px] text-gray-400">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  const kpis = [
    { label: 'Total Revenue', value: 'LKR 4.2M', change: '+10.5%', up: true, sub: 'vs last month' },
    { label: 'Gross Profit', value: 'LKR 1.68M', change: '+10.5%', up: true, sub: 'Margin: 40%' },
    { label: 'Total Customers', value: '284', change: '+12', up: true, sub: 'This month' },
    { label: 'Active Projects', value: '18', change: '+3', up: true, sub: '4 completing soon' },
    { label: 'Avg Deal Value', value: 'LKR 89K', change: '-3.6%', up: false, sub: 'vs last month' },
    { label: 'Inventory Value', value: 'LKR 2.8M', change: '-5.2%', up: false, sub: '48 SKUs' },
  ];

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200">
            <BarChart2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Business Analytics</h1>
            <p className="text-xs text-gray-500">AI-powered insights · Sales forecasting · Performance metrics</p>
          </div>
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden">
          {(['7d', '30d', '90d', '1y'] as const).map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-2 text-xs font-medium transition-colors ${period === p ? 'bg-indigo-500 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {kpis.map((kpi, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-2xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">{kpi.label}</p>
            <p className="text-lg font-bold text-gray-900">{kpi.value}</p>
            <div className={`flex items-center gap-1 mt-0.5 text-xs font-medium ${kpi.up ? 'text-green-600' : 'text-red-500'}`}>
              {kpi.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {kpi.change}
            </div>
            <p className="text-[10px] text-gray-400 mt-0.5">{kpi.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Revenue vs Expenses vs Profit</h2>
            <span className="text-xs text-gray-400">Jan – Jul 2026</span>
          </div>
          <div className="flex items-end gap-2 h-32 mt-2">
            {months.map((month, i) => (
              <div key={i} className="flex-1 flex flex-col gap-0.5 items-center">
                <div className="w-full space-y-0.5 flex flex-col justify-end h-28">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${(revenue[i] / 5) * 100}%` }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className="w-full bg-indigo-500 rounded-t-sm opacity-80"
                  />
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${(expenses[i] / 5) * 80}%` }}
                    transition={{ duration: 0.6, delay: i * 0.08 + 0.1 }}
                    className="w-full bg-red-300 rounded-t-sm opacity-60"
                    style={{ marginTop: '-100%' }}
                  />
                </div>
                <span className="text-[9px] text-gray-400 mt-1">{month}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3">
            {[['Revenue', 'bg-indigo-500'], ['Expenses', 'bg-red-300'], ['Profit', 'bg-green-400']].map(([label, color]) => (
              <div key={label as string} className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className={`w-2.5 h-2.5 rounded-sm ${color}`} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* AI Insights */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-violet-500" />
            <h2 className="text-sm font-semibold text-gray-900">AI Insights</h2>
          </div>
          <div className="space-y-3">
            {aiInsights.map((insight, i) => {
              const Icon = insight.icon;
              return (
                <div key={i} className={`p-3 rounded-xl border ${insight.color}`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${insight.color.split(' ')[0]}`} />
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{insight.title}</p>
                      <p className="text-[11px] text-gray-600 mt-0.5 leading-relaxed">{insight.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Products */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Best Selling Products</h2>
          <div className="space-y-3">
            {topProducts.map((product, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-900">{product.name}</span>
                    <span className={`text-xs font-medium flex items-center gap-0.5 ${product.trend > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {product.trend > 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {Math.abs(product.trend)}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(product.revenue / topProducts[0].revenue) * 100}%` }}
                      transition={{ duration: 0.8, delay: i * 0.1 }}
                      className={`h-full rounded-full ${product.color}`}
                    />
                  </div>
                </div>
                <span className="text-xs text-gray-500 w-16 text-right">LKR {(product.revenue / 1000).toFixed(0)}K</span>
              </div>
            ))}
          </div>
        </div>

        {/* Technician Performance */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Technician Performance</h2>
          <div className="space-y-3">
            {techPerformance.map((tech, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                  {tech.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-900">{tech.name}</span>
                    <span className="text-xs font-medium text-amber-600">★ {tech.rating}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[11px] text-gray-500">{tech.jobs} jobs</span>
                    <span className="text-[11px] text-gray-500">LKR {(tech.revenue / 1000).toFixed(0)}K</span>
                    <span className={`text-[11px] flex items-center gap-0.5 ${tech.trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
                      {tech.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {tech.trend === 'up' ? 'Growing' : 'Declining'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sales Forecast */}
      <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5" />
          <h2 className="text-sm font-semibold">AI Sales Forecast</h2>
          <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">Powered by K-Tec Intelligence</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {forecast.map((f, i) => (
            <div key={i} className="bg-white/10 rounded-2xl p-4">
              <p className="text-xs opacity-80 mb-1">{f.month}</p>
              <p className="text-xl font-bold">LKR {(f.revenue / 1000000).toFixed(1)}M</p>
              <div className="mt-2">
                <div className="flex items-center justify-between text-[11px] opacity-80 mb-1">
                  <span>Confidence</span>
                  <span>{f.confidence}%</span>
                </div>
                <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/70 rounded-full" style={{ width: `${f.confidence}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs opacity-70 mt-3">Based on historical data, seasonal trends, and current pipeline analysis. Updated daily.</p>
      </div>
    </div>
  );
}
